// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from './component';

const _registeredComponents = {};
let _firedOnReady = false;
let _onReadyEvents = null;

/**
 * @class Jsw
 * The Jsw namespace (global object) encapsulates all classes, singletons, and
 * utility methods provided by Jsw's library.
 * @singleton
 */
const Jsw = {
    version: '1.0',
    baseUrl: '',
    ctxIdName: '',
    ctxId: '',
    uiContext: $H(),

    priority: {
        high: 'high',
        normal: 'normal',
        low: 'low',
    },

    isRtl() {
        return document.documentElement.dir === 'rtl';
    },

    registerComponent(identity, component) {
        _registeredComponents[identity] = component;
    },

    /**
     * Returns an component by identity.
     * @param {String} identity The identity of the component.
     * @returns {Object} The component, undefined if not found.
     */
    getComponent(identity) {
        return _registeredComponents[identity];
    },

    /**
     * Creates a new Component from the specified config object using the config object's
     * `componentType` to determine the class to instantiate.
     * @param {Object} config A configuration object for the Component you wish to create.
     * @returns {Jsw.Component} The newly instantiated Component.
     */
    createComponent(config) {
        if (!config.componentType || config instanceof Component) {
            return config;
        }

        const Cls = Jsw.namespace(config.componentType, false);
        if (!Cls) {
            throw new Error(`Unrecognized class name: ${config.componentType}`);
        }
        return new Cls(config);
    },

    clearStatusMessages() {
        const contentElement = $('main');
        if (!contentElement) {
            return;
        }
        ['.msg-error', '.msg-info', '.msg-warning'].each(function (msgClass) {
            contentElement.select(msgClass).each(function (messageElement) {
                messageElement.remove();
            });
        });
    },

    addStatusMessage(type, message, params) {
        params = ('undefined' !== typeof params) ? params : {};

        const elementParams = { class: `msg-box msg-${type}` };
        if (params.id) {
            elementParams.id = params.id;
        }

        if (params.title) {
            message = `<b>${params.title}:</b> ${message}`;
        }

        const statusElement = new Element('div', elementParams);
        statusElement.update(
            '<div><div><div><div><div>' +
            `<div class="msg-content">${message}</div>` +
            '</div></div></div></div></div>'
        );

        if (params.closable) {
            const closeElement = new Element('span', { class: 'close' });
            closeElement.observe('click', params.onClose);
            statusElement.down('.msg-content').insert({ top: closeElement });
        }

        try {
            let target;
            if (params.renderTo) {
                target = params.renderTo;
            } else {
                target = 'main';
            }
            let mode = 'top';
            if (params.renderMode) {
                mode = params.renderMode;
            }
            const insertion = {};
            insertion[mode] = statusElement;

            $(target).insert(insertion);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(`${type} | ${message}`);
        }
    },

    /**
     * Adds a listener to be notified when the document is ready (before onload and before images are loaded).
     * @param {String|Function} [priority]
     * @param {Function} callback The method to call.
     */
    onReady(priority, callback) {
        if ('function' === typeof priority) {
            callback = priority;
            priority = Jsw.priority.normal;
        }

        const wrappedCallback = () => {
            try {
                callback();
            } catch (e) {
                // eslint-disable-next-line no-console
                console && console.error(e);
            }
        };

        if (_firedOnReady) {
            wrappedCallback();
            return;
        }

        if (!_onReadyEvents) {
            Event.observe(window, 'load', Jsw._fireOnReady);
            _onReadyEvents = {};
        }

        if (!_onReadyEvents[priority]) {
            _onReadyEvents[priority] = [];
        }

        _onReadyEvents[priority].push(wrappedCallback);
    },

    _fireOnReady() {
        const predefinedPriorities = [Jsw.priority.low, Jsw.priority.normal, Jsw.priority.high];
        Object.keys(_onReadyEvents).sort(function (a, b) {
            return predefinedPriorities.indexOf(b) - predefinedPriorities.indexOf(a);
        }).forEach(function (priority) {
            _onReadyEvents[priority].forEach(Function.prototype.call, Function.prototype.call);
        });

        _firedOnReady = true;
    },

    /**
     * Creates namespaces to be used for scoping variables and classes so that they are not global.
     * Specifying the last node of a namespace implicitly creates all other nodes. Usage:
     *
     *     Jsw.namespace('Jsw.list');
     *
     * @param {String} ns
     * @param {Boolean} autoCreate=true
     * @return {Object} The object that is the namespace or class name.
     */
    namespace(ns, autoCreate = true) {
        const nsParts = ns.split('.');
        let root = window;

        for (let partIndex = 0; partIndex < nsParts.length; partIndex++) {
            if (typeof root[nsParts[partIndex]] === 'undefined') {
                if (autoCreate) {
                    root[nsParts[partIndex]] = {};
                } else {
                    return undefined;
                }
            }

            root = root[nsParts[partIndex]];
        }

        return root;
    },

    prepareUrl(url) {
        if (typeof url !== 'string') {
            return null;
        }

        if (
            url.startsWith('/services/') ||
            url.startsWith('/server/spamassassin') ||
            url.startsWith('/server/mail_') ||
            url.startsWith('/plesk/') ||
            url.startsWith('/admin/') ||
            url.startsWith('/smb/') ||
            (Jsw.baseUrl !== '' && url.startsWith(`${Jsw.baseUrl}/`)) ||
            (!url.startsWith('/') && (url.substr(0, 2) !== '^/')) ||
            url.startsWith('/modules/')
        ) {
            return url;
        }
        if (url.substr(0, 2) === '^/') {
            return url.toString().substr(1);
        }

        return Jsw.baseUrl + url;
    },

    addUrlParams(url, params) {
        if ('string' === typeof params) {
            const splitUrl = url.split('?', 2);
            params = params.startsWith('/') || params.startsWith('?') ? params : `?${params}`;
            const splitParams = params.split('?', 2);
            url = splitUrl[0].replace(/\/+$/, '') + splitParams[0];
            params = Object.extend(
                (splitUrl[1] || '').toQueryParams(),
                (splitParams[1] || '').toQueryParams()
            );
        }

        if (Object.prototype.toString.call(params) === '[object Object]') {
            params = Object.keys(params).map(function (name) {
                if ('undefined' === typeof params[name]) {
                    return name;
                }
                return `${name}=${encodeURIComponent(params[name])}`;
            }).join('&');
        }

        if (!params) {
            return url;
        }
        return url + (url.include('?') ? '&' : '?') + params;
    },

    showInternalError(message) {
        Jsw.clearStatusMessages();
        Jsw.addStatusMessage('error', `Internal error ;-P<pre>${message.escapeHTML()}</pre>`);
    },

    /**
     * Helper for render components/elements/strings
     * @experimental Be careful with this method, its implementation is not yet complete.
     *
     * @param {Element} parentElement
     * @param {Jsw.Component|Element|Object|String} element
     * @param {String} [renderMode=bottom"] above|below|top|bottom|inner
     */
    render(parentElement, element, renderMode) {
        if (renderMode === 'inner') {
            parentElement.update('');
            renderMode = undefined;
        }

        if (Object.isArray(element)) {
            element.forEach(function (element) {
                Jsw.render(parentElement, element, renderMode);
            });
            return;
        }

        if (element instanceof Component) {
            element.setRenderTarget(parentElement);
            if (renderMode) {
                element._renderMode = renderMode;
            }
            element.render();
            return;
        }

        if (Object.prototype.toString.call(element) === '[object Object]') {
            const attrs = Object.extend({}, element.attrs);
            Object.keys(attrs).forEach(function (attr) {
                if ('undefined' === typeof attrs[attr]) {
                    delete attrs[attr];
                }
            });

            const renderFn = Object.isFunction(attrs.onrender) ? attrs.onrender : Jsw.emptyFn;
            delete attrs.onrender;

            const events = {};
            Object.keys(attrs).forEach(function (attr) {
                if (Object.isFunction(attrs[attr]) && attr.indexOf('on') === 0) {
                    events[attr.slice(2)] = attrs[attr];
                    delete attrs[attr];
                }
            });

            const el = new Element(element.tag || element._tag, attrs);
            Object.keys(events).forEach(function (eventName) {
                el.observe(eventName, events[eventName]);
            });

            Jsw.render(el, element.children);

            element = el;
            renderFn(element);
        }

        const insertion = {};
        insertion[renderMode || 'bottom'] = element;
        parentElement.insert(insertion);
    },

    /**
     * Helper for create elements
     * @experimental Be careful with this method, its implementation is not yet complete.
     *
     * @param {String} selector
     * @param {Object} [attrs]
     * @param {String[]|String...|Object[]|Object...} [children]
     * @returns {Object}
     */
    createElement(selector, ...args) {
        const hasAttrs = Object.prototype.toString.call(args[0]) === '[object Object]'
            && !('tag' in args[0])
            && !(args[0] instanceof Component);
        let tag = 'div';
        const classes = [];
        const selectorRe = /(?:(^|#|\.)([^#\.\[\]]+))|(\[.+?\])/g;
        const attrRe = /\[(.+?)(?:=("|'|)(.*?)\2)?\]/;
        let match;
        const attrs = hasAttrs ? args.shift() : {};

        while ((match = selectorRe.exec(selector))) {
            if (match[1] === '' && match[2]) {
                tag = match[2];
            } else if (match[1] === '#') {
                attrs.id = match[2];
            } else if (match[1] === '.') {
                classes.push(match[2]);
            } else if (match[3][0] === '[') {
                const pair = attrRe.exec(match[3]);
                attrs[pair[1]] = pair[3] || (pair[2] ? '' : true);
            }
        }

        if (typeof attrs.class === 'string' && attrs.class !== '') {
            classes.push(attrs.class);
        }
        if (classes.length) {
            attrs.class = classes.join(' ');
        }

        const children = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;

        return {
            tag,
            attrs,
            children,
        };
    },

    /**
     * Escape element attributes for html concatenation
     *
     * @param {String} value
     * @returns {String}
     */
    escapeAttribute(value) {
        return value.toString().escapeHTML().replace(/"/g, '&quot;');
    },

    emptyFn() {
    },
};

Jsw.keyCode = {
    ESC: 27,
    ENTER: 13,
    LEFT_ARROW: 37,
    UP_ARROW: 38,
    RIGHT_ARROW: 39,
    DOWN_ARROW: 40,
};

// Monkey patching Prototype for proper handling of session expiration
Ajax.PatchedRequest = Class.create(Ajax.Request, {
    initialize($super, url, options) {
        options = Object.extend({
            onException(transport, exception) {
                throw exception;
            },
            on400() {
                top.window.location.reload();
            },
            on500(transport) {
                Jsw.showInternalError(transport.responseText);
            },
            on0() {
                // do nothing in case of internal/network error
                return false;
            },
        }, options || {});

        $super(url, options);
    },
    abort() {
        this.transport.abort();
    },
});

Ajax.Request.prototype = Ajax.PatchedRequest.prototype;

export const {
    createComponent, registerComponent, getComponent,
    addStatusMessage, clearStatusMessages, showInternalError,
    render, createElement,
    emptyFn, escapeAttribute, isRtl,
    prepareUrl, addUrlParams,
    onReady, namespace, _fireOnReady,
    baseUrl,
    keyCode, priority,
} = Jsw;
export default Jsw;



// WEBPACK FOOTER //
// ./jsw/common.js